import React from 'react';

import analytics from '@repo/analytics';
import {
  useUserStore,
  userStoreSelector,
} from '@repo/editor/src/stores/userStore';
import { withEventScope } from '@repo/event-processor';
import { Button } from '@repo/ui/components/Buttons';
import UploadIcon from '@repo/ui/components/Icon/UploadIcon';
import { BREAKPOINTS } from '@repo/ui/queries';
import { themeStyle } from '@repo/ui/theming';
import { BlocksContent } from '@strapi/blocks-react-renderer';

import { getCurrentUseCase } from 'components/DesignsListPage/v1/utils';
import { CATEGORY_UPLOAD_CLICK } from 'global/analyticsEvents';
import { OPENED_UPLOAD_TOOLBAR_EDITOR_URL } from 'global/urlUtils';
import TextBlock from 'strapi/component/shared/TextBlock/TextBlock';
import { LayoutSwitch } from 'styles/queries';

import CategorySection from '../CategorySection/CategorySection';
import SearchSection from '../SearchSection/SearchSection';

import {
  CoverHeadline,
  CoverUnauthorisedLibraryDescription,
  CoverUnauthorisedLibraryHeadline,
  CoverUnauthorisedUseCaseHeadline,
  UploadButtonTheme,
} from './config';
import { Content, Description, TextWrapper, Headline, Wrapper } from './styles';
import { HeaderSectionProps } from './type';

const HeaderSection: React.FC<HeaderSectionProps> = ({
  allUseCases,
  setFilters,
  currentSelection,
  ...props
}) => {
  const user = useUserStore(userStoreSelector.loadingUser);
  const {
    useCaseSlug: currentUseCaseSlug,
    subUseCaseSlug: currentSubUseCaseSlug,
    categorySlug: currentCategorySlug,
    subCategorySlug: currentSubCategorySlug,
  } = currentSelection;

  const onUploadButtonClick = (): void => {
    void analytics.track(CATEGORY_UPLOAD_CLICK);
    window.open(OPENED_UPLOAD_TOOLBAR_EDITOR_URL, '_blank');
  };

  let headline = CoverHeadline;
  let description;
  if (!user) {
    if (props.headline && props.description) {
      headline = props.headline;
      description = props.description;
    } else if (props.headline) {
      headline = props.headline;
    } else {
      const currentUseCase = getCurrentUseCase(
        allUseCases,
        currentCategorySlug,
        currentSubCategorySlug,
        currentUseCaseSlug,
        currentSubUseCaseSlug
      );
      headline = currentUseCase?.name
        ? CoverUnauthorisedUseCaseHeadline(currentUseCase?.name)
        : CoverUnauthorisedLibraryHeadline;
      description = !currentUseCase
        ? CoverUnauthorisedLibraryDescription
        : undefined;
    }
  }

  return (
    <Wrapper>
      {!!user && (
        <LayoutSwitch
          breakpoints={[BREAKPOINTS.largeTabletMin]}
          variationProps={[
            {
              props: { label: 'Upload', width: 'min-content' },
            },
            {
              props: {
                icon: { component: UploadIcon, height: '16px' },
                width: '40px',
              },
            },
          ]}
        >
          <Button
            height="40px"
            name="headerUploadButton"
            style={{
              position: 'absolute',
              top: '16px',
              right: '18px',
              borderRadius: '8px',
            }}
            theme={UploadButtonTheme}
            onClick={onUploadButtonClick}
          />
        </LayoutSwitch>
      )}
      <Content enableTopMargin={!!user}>
        <TextWrapper>
          {/* remove suppressHydrationWarning after completely moving to the app folder*/}
          <Headline suppressHydrationWarning color={themeStyle.backgroundAlt}>
            {headline}
          </Headline>
          {description && !user && (
            <Description suppressHydrationWarning>
              {/* remove suppressHydrationWarning after completely moving to the app folder*/}
              {typeof description === 'string' ? (
                description
              ) : (
                <TextBlock
                  color={themeStyle.backgroundAlt}
                  paragraphType="p1"
                  text={description as BlocksContent}
                />
              )}
            </Description>
          )}
        </TextWrapper>
        <CategorySection allUseCases={allUseCases} />
      </Content>
      <SearchSection setFilters={setFilters} />
    </Wrapper>
  );
};

export default withEventScope(HeaderSection, { feature: 'designsListHeader' });
